/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ArrowBack from '@mui/icons-material/ArrowBackIosNew';
import ArrowNext from '@mui/icons-material/ArrowForwardIos';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import LoadsService from '../../providers/Loads/Loads.service';
import NumberUtils from '../../utils/numberUtils';
import heic2any from 'heic2any';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import { PaperProps } from '@mui/material/Paper/Paper';
import TwBackdrop from '../TwBackdrop/TwBackdrop';
import { USER_ROLE } from '../../providers/User/User.model';
import useAuth from '../../providers/Auth/Auth.provider';
import TwGenericMenu, { TwGenericMenuOptions } from '../TwGenericMenu/TwGenericMenu';
import { OrderAttachment } from '../../redux/api/orders/orders.model';
import TwMobileMenuViewer from './TwMobileMenuViewer';
import '../TwDocumentViewer/TwDocumentViewer.scss';
import GuestService from '../../providers/Guest/Guest.service';
import FileUtils from '../../utils/fileUtils';
import { isWebInFlutter } from '../../utils/browserUtils';
enum TwDocumentType {
  pdf = 1,
  image = 2
}

interface TwDocumentOrderTripsViewerProps {
  /**
   * Entity for the document. This is used to generate the file name when downloading the file.
   */
  entity?: 'Order' | 'Trip';
  /**
   * Load readable id to show in downloaded file name
   */
  readableId?: number | string;
  /**
   * Email id for forwarded emails file preview
   */
  emailId?: string;
  /**
   * Selected file id
   */
  selectedFileId?: string;
  /**
   * Sets the viewer open if true.
   */
  open: boolean;
  /**
   * PDF or image file list.
   */
  files: OrderAttachment[] | null;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClickClose: () => void;
  /**
   * Blob
   */
  blob?: Blob;
  /**
   * Type of Blob: pdf or image
   */
  blobType?: TwDocumentType;
  /**
   * Title for Blob
   */
  blobTitle?: string;
  /**
   * Material Paper props
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  PaperProps?: Partial<PaperProps<'div', {}>>;
  /**
   * ClassName for the viewer
   */
  className?: string;
  /**
   * boolean for change link if the file is public
   */
  isPublic?: boolean;
  /**
   * token for public
   */
  token?: string;
  /**
   * Callback for on delete file
   */
  onDelete?: (document: OrderAttachment) => void;
  /**
   * driver id if from driver link
   */
  driverId?: string;
  hideCloseIcon?: boolean;
}

export const getOrderTripFileName = (file: OrderAttachment | null): string => {
  if (file && file.attachment) {
    if (file.attachment.name) {
      return file.attachment.name;
    } else {
      return file.attachment.key;
    }
  }
  return '';
};

const TwOrderTripsDocViewer: React.FC<TwDocumentOrderTripsViewerProps> = ({
  entity = 'Order',
  readableId,
  emailId,
  selectedFileId,
  open,
  files,
  PaperProps,
  className,
  onClickClose,
  blob,
  blobType,
  blobTitle,
  isPublic,
  token,
  onDelete,
  driverId,
  hideCloseIcon
}): JSX.Element => {
  const counter = useRef(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [fileSrc, setFileSrc] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<any>(null);
  const [pagesElements, setPagesElements] = useState<JSX.Element[]>([]);
  const [imgPage, setImgPage] = useState<number>(0);
  const matchesMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const matchesDesktopLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
  const { data } = useAuth();

  const isFlutterApp = isWebInFlutter();

  const showMenu = (): boolean => {
    // hide in desktop
    if (!matchesMobile || !onDelete) return false;

    // if admin/ dispatcher / owner show menu
    if (
      data?.userData?.role &&
      [USER_ROLE.ADMIN, USER_ROLE.OWNER, USER_ROLE.DISPATCHER].includes(data?.userData?.role)
    ) {
      return true;
    }

    const currentFile = files && files[imgPage - 1];
    // if driver public link and driver link's driver uploaded file then show menu
    if (
      isPublic &&
      driverId &&
      currentFile &&
      'driver' in currentFile &&
      currentFile?.driver_id === driverId
    ) {
      return true;
    }

    // if driver login show menu if they uploaded the file
    if (
      data?.userData?.role === USER_ROLE.DRIVER &&
      currentFile &&
      'driver' in currentFile &&
      currentFile?.driver_id === data?.userData?.driver?.id
    ) {
      return true;
    }

    return false;
  };

  const dialogClasses = className
    ? `${className} tw-doc-viewer__dialog tid-document-viewer`
    : ` tw-doc-viewer__dialog tid-document-viewer`;

  useEffect(() => {
    if (!files && !selectedFileId) return;
    const selectedFileIndex = (files as Array<OrderAttachment>).findIndex((file) => {
      return file.id === selectedFileId;
    });
    selectedFileId ? setImgPage(selectedFileIndex + 1) : setImgPage(0);
  }, [selectedFileId, files]);

  useEffect(() => {
    if (!files || files.length === 0 || !open) setLoading(true);
    if (files && files.length !== 0 && open && selectedFileId) {
      setFileSrc(null);
      !selectedFileId && !imgPage && setImgPage(1);
      setLoading(true);
      (async (): Promise<void> => {
        if (emailId) {
          const src = files[imgPage - 1] && (await getEmailFileSrc(files[imgPage - 1]));
          setFileSrc(src);
          setLoading(false);
        } else {
          const currentFile = files[imgPage - 1];
          // Try using s3_signed_url first
          if (currentFile && currentFile?.attachment?.s3_signed_url) {
            setFileSrc(currentFile.attachment.s3_signed_url);
          } else if (currentFile) {
            // Fall back to existing method if signed URL isn't available
            const src = await getFileSrc(currentFile.attachment.key, currentFile.id);
            setFileSrc(src);
            setLoading(false);
          }
        }
      })();
    }
  }, [files, imgPage, open, selectedFileId]);

  useEffect(() => {
    if (!blob || !blobType) {
      setLoading(true);
    }
    if (blob && blobType) {
      setFileSrc(null);
      setLoading(true);
      (async (): Promise<void> => {
        const src = await getBlobSrc(blob);
        if (src) {
          setFileSrc(src);
          setLoading(false);
        }
      })();
    }
  }, [blob, blobType]);

  useEffect(() => {
    const pages: JSX.Element[] = [];
    if (numPages) {
      Array.from(Array(numPages).keys()).map((item, i) => {
        pages.push(
          <Page
            scale={matchesMobile ? 0.45 : matchesDesktopLarge ? 1.7 : 1}
            className="tw-doc-viewer__viewerPdfPage"
            key={i}
            pageNumber={i + 1}
            renderAnnotationLayer={false}
            renderTextLayer
            onClick={(e: any): void => e.stopPropagation()}
          />
        );
      });
      setPagesElements(pages);
    }
  }, [numPages]);

  const handleClose = (e: any, reason: any): void => {
    e.stopPropagation();
    if (reason && reason == 'backdropClick') return;
    setFileSrc(null);
    setNumPages(null);
    setLoading(true);
    onClickClose && onClickClose();
  };

  const handleNextDoc = (e: any): void => {
    e.stopPropagation();
    if (!files) return;
    setImgPage((prev) => (prev < files.length ? prev + 1 : prev));
  };

  const handlePrevDoc = (e: any): void => {
    e.stopPropagation();
    if (!files) return;
    setImgPage((prev) => (prev <= files.length && prev > 1 ? prev - 1 : prev));
  };

  const getFileSrc = async (key: string, attachmentId: string): Promise<string> => {
    try {
      const imgBlob =
        isPublic && token
          ? await GuestService.getLoadFile(token, attachmentId)
          : await LoadsService.getAttachmentFile(attachmentId);

      if (key.includes('heic') && (imgBlob as Blob)) {
        const heicBlob = await heic2any({ blob: imgBlob, toType: 'image/jpeg' });
        return URL.createObjectURL(heicBlob as Blob);
      }
      return URL.createObjectURL(imgBlob);
    } catch (error) {
      return '';
    } finally {
      setLoading(false);
    }
  };

  const getEmailFileSrc = async (file: OrderAttachment): Promise<string> => {
    if (!emailId) return '';

    try {
      const imgBlob = await LoadsService.getEmailFile(emailId, file.id);
      if (file.attachment.file_type.includes('heic') && imgBlob) {
        const heicBlob = await heic2any({ blob: imgBlob, toType: 'image/jpeg' });
        return URL.createObjectURL(heicBlob as Blob);
      }
      return URL.createObjectURL(imgBlob);
    } catch (error) {
      return '';
    }
  };

  const getBlobSrc = async (blob: Blob): Promise<string> => {
    if (blobType === TwDocumentType.image) {
      try {
        const heicBlob = await heic2any({ blob, toType: 'image/jpeg' });
        return heicBlob && URL.createObjectURL(heicBlob as Blob);
      } catch (err) {
        console.error(err);
      }
    }
    return URL.createObjectURL(blob);
  };

  const getPreview = (file?: OrderAttachment | null): JSX.Element => {
    counter.current += 1;
    let docIsPhoto = blobType === TwDocumentType.image;
    let docIsPdf = blobType === TwDocumentType.pdf;

    if (file) {
      docIsPhoto = file.attachment.file_type.includes('image');
      docIsPdf =
        file.attachment.file_type.includes('pdf') ||
        file.attachment.file_type.includes('octet-stream');
    }

    if (fileSrc && docIsPhoto) {
      return (
        <img
          src={fileSrc}
          width="400px"
          className={`tw-doc-viewer__viewerPhoto tid-document-viewer__photo`}
          onClick={(e: any): void => e.stopPropagation()}
          onLoad={(): void => setLoading(false)}
        />
      );
    } else if (fileSrc && docIsPdf) {
      return (
        <Document
          file={fileSrc}
          onLoadSuccess={onPDFLoadSuccess}
          className={`${
            numPages <= 1 && matchesMobile
              ? 'tw-doc-viewer__justify-center'
              : 'tw-doc-viewer__justify-start'
          } tw-doc-viewer__viewerDoc tid-document-viewer__pdf`}
        >
          {pagesElements}
        </Document>
      );
    }

    if (!loading && !fileSrc) {
      return <span className="tw-doc-viewer__error">Could not load preview</span>;
    }

    return <></>;
  };

  const onPDFLoadSuccess = (params: any): void => {
    setNumPages(params._pdfInfo.numPages);
    setLoading(false);
  };

  const checkNameOrKeyHasExtension = (stringToCheck: string, type: string): string => {
    const split = type.split('/');
    const extension = split[split.length - 1];

    if (stringToCheck.includes('.')) {
      return stringToCheck;
    } else {
      return stringToCheck + `.${extension}`;
    }
  };

  const handleDownload = (file: OrderAttachment | null, url: string | null): void => {
    if (file?.attachment?.s3_signed_download_url) {
      FileUtils.fetchAndDownloadFileLinkClick(file.attachment.s3_signed_download_url);
    } else {
      const link = document.createElement('a');
      link.href = url || '';
      link.download = `${entity}#${readableId}`;
      const attachment = file?.attachment;

      if (attachment && 'name' in attachment && attachment.name) {
        link.download = checkNameOrKeyHasExtension(
          `${entity}#${readableId}-${attachment.name}`,
          attachment.file_type
        );
      } else if (attachment && 'key' in attachment && attachment.key) {
        link.download = checkNameOrKeyHasExtension(
          `${entity}#${readableId}-${attachment.key}`,
          attachment.file_type
        );
      } else {
        link.download = `${entity}#${readableId}-invoice.pdf`;
      }

      link.click();
      setTimeout(function () {
        // For Firefox it is necessary to delay revoking the ObjectURL
        url && window.URL.revokeObjectURL(url);
      }, 100);
    }
  };

  const options: TwGenericMenuOptions[] = [
    {
      label: 'Delete',
      className: 'detail-file__delete',
      action: (): void => {
        onDelete && files && files.length > 0 && onDelete(files[imgPage - 1]);
      }
    }
  ];

  return (
    <Dialog
      className={dialogClasses}
      open={open}
      onClose={handleClose}
      fullScreen
      fullWidth={true}
      hideBackdrop
      disableEnforceFocus
      PaperProps={PaperProps}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          {showMenu() && (
            <TwGenericMenu
              className="detail-file__menu"
              options={options}
              menuOwnerSection={
                <TwMobileMenuViewer file={files && files[imgPage - 1]} fileSrc={fileSrc} />
              }
            />
          )}

          <DialogTitle className={`tw-doc-viewer__title tid-document-viewer__title`}>
            <InsertDriveFileOutlinedIcon className="tw-doc-viewer__titleIcon" />
            <div className="tw-doc-viewer__fileName">
              {files ? getOrderTripFileName(files[imgPage - 1]) : blobTitle}
            </div>

            {files && files[imgPage - 1] ? (
              <span className="tw-doc-viewer__fileSize">
                {NumberUtils.getFileSize(files[imgPage - 1].attachment.size)}
              </span>
            ) : (
              blob && (
                <span className="tw-doc-viewer__fileSize">
                  {NumberUtils.getFileSize(blob.size)}
                </span>
              )
            )}
          </DialogTitle>
          {files && (
            <span className="tw-doc-viewer__viewerFileCount">
              File {imgPage} of {files?.length}
            </span>
          )}
          <div className={`tw-doc-viewer__actions tid-document-viewer__actions`}>
            <IconButton
              className="tid-document-viewer__download-btn"
              edge="start"
              onClick={(e): void => {
                e.stopPropagation();
                files && files[imgPage - 1]
                  ? handleDownload(files[imgPage - 1], fileSrc)
                  : handleDownload(null, fileSrc);
              }}
              aria-label="download"
            >
              <DownloadIcon className="tw-doc-viewer__actionItem" />
            </IconButton>

            {/* TODO: Enable icon when print feature is developed */}
            {/* <IconButton edge="start" onClick={handlePrint} aria-label="print">
              <PrintIcon className={classes.actionItem} />
            </IconButton> */}
            {!hideCloseIcon && (
              <IconButton edge="start" onClick={onClickClose} aria-label="close">
                <CloseIcon />
              </IconButton>
            )}
          </div>
        </Toolbar>
      </AppBar>
      <DialogContent
        className={`tw-doc-viewer__content tid-document-viewer__content`}
        onClick={(e: any): void => handleClose(e, null)}
      >
        {files && files.length > 0 && imgPage > 1 && (
          <IconButton
            edge="start"
            onClick={handlePrevDoc}
            aria-label="previous"
            sx={{
              left: '20px'
            }}
            className="tw-doc-viewer__viewerNav"
          >
            <ArrowBack />
          </IconButton>
        )}
        {!!files && selectedFileId && getPreview(files[imgPage - 1])}
        {!!blob && !selectedFileId && getPreview()}
        <TwBackdrop isOpen={loading} className="tid-document-viewer__loader" position="absolute" />

        {files && files.length > 0 && imgPage < files.length && (
          <IconButton
            edge="start"
            onClick={handleNextDoc}
            aria-label="next"
            sx={{ right: '10px' }}
            className="tw-doc-viewer__viewerNav"
          >
            <ArrowNext />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwOrderTripsDocViewer;
